import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const CATEGORIES = [
  'business', 'leadership', 'marketing', 'sales', 'finance',
  'strategy', 'management', 'productivity', 'entrepreneurship', 'personal-development'
];

const BookFormModal = ({ book, onClose, onSaved }) => {
  const isEdit = !!book?.id;
  const [form, setForm] = useState({
    title: '', author: '', description: '', price: '',
    cover_image: '', cover_image_alt: '', category: 'business',
    frameworks: '', external_link: '', is_new: false, is_bestseller: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [uploadingCover, setUploadingCover] = useState(false);

  useEffect(() => {
    if (book) {
      setForm({
        title: book?.title || '',
        author: book?.author || '',
        description: book?.description || '',
        price: book?.price?.toString() || '',
        cover_image: book?.cover_image || '',
        cover_image_alt: book?.cover_image_alt || '',
        category: book?.category || 'business',
        frameworks: Array.isArray(book?.frameworks) ? book?.frameworks?.join(', ') : '',
        external_link: book?.external_link || '',
        is_new: book?.is_new || false,
        is_bestseller: book?.is_bestseller || false,
      });
      setCoverPreview(book?.cover_image || '');
    }
  }, [book]);

  const handleCoverChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const uploadCover = async () => {
    if (!coverFile) return form?.cover_image;
    setUploadingCover(true);
    try {
      const ext = coverFile?.name?.split('.')?.pop();
      const fileName = `cover-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase?.storage?.from('book-covers')?.upload(fileName, coverFile, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase?.storage?.from('book-covers')?.getPublicUrl(fileName);
      return publicUrl;
    } catch (err) {
      throw new Error('Cover upload failed: ' + err?.message);
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);
    try {
      let coverUrl = form?.cover_image;
      if (coverFile) {
        coverUrl = await uploadCover();
      }
      const frameworksArr = form?.frameworks
        ? form?.frameworks?.split(',')?.map((f) => f?.trim())?.filter(Boolean)
        : [];
      const payload = {
        title: form?.title,
        author: form?.author,
        description: form?.description,
        price: parseFloat(form?.price),
        cover_image: coverUrl,
        cover_image_alt: form?.cover_image_alt || form?.title + ' book cover',
        category: form?.category,
        frameworks: frameworksArr,
        external_link: form?.external_link || null,
        is_new: form?.is_new,
        is_bestseller: form?.is_bestseller,
      };
      let result;
      if (isEdit) {
        result = await supabase?.from('ebooks')?.update(payload)?.eq('id', book?.id)?.select()?.single();
      } else {
        result = await supabase?.from('ebooks')?.insert(payload)?.select()?.single();
      }
      if (result?.error) throw new Error(result.error.message);
      onSaved(result?.data);
    } catch (err) {
      setError(err?.message || 'Failed to save book.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="text-lg font-heading font-semibold text-foreground">
            {isEdit ? 'Edit Book' : 'Add New Book'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Cover Image */}
          <div className="flex gap-4">
            <div className="w-24 h-32 bg-muted rounded-lg overflow-hidden flex-shrink-0 border border-border">
              {coverPreview ? (
                <img src={coverPreview} alt="Cover preview" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon name="Image" size={24} color="var(--color-muted-foreground)" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-foreground mb-1.5">Cover Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleCoverChange}
                className="block w-full text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
              />
              <p className="text-xs text-muted-foreground mt-1">Or enter URL below</p>
              <input
                type="text"
                value={form?.cover_image}
                onChange={(e) => setForm({ ...form, cover_image: e?.target?.value })}
                placeholder="/assets/images/cover.jpg or https://..."
                className="mt-2 w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form?.title}
              onChange={(e) => setForm({ ...form, title: e?.target?.value })}
              required
              placeholder="Book title"
              className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>

          {/* Author */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Author <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form?.author}
              onChange={(e) => setForm({ ...form, author: e?.target?.value })}
              required
              placeholder="Author name"
              className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Description <span className="text-red-500">*</span></label>
            <textarea
              value={form?.description}
              onChange={(e) => setForm({ ...form, description: e?.target?.value })}
              required
              rows={4}
              placeholder="Book description..."
              className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
            />
          </div>

          {/* Price & Category */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Price ($) <span className="text-red-500">*</span></label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form?.price}
                onChange={(e) => setForm({ ...form, price: e?.target?.value })}
                required
                placeholder="9.99"
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Category <span className="text-red-500">*</span></label>
              <select
                value={form?.category}
                onChange={(e) => setForm({ ...form, category: e?.target?.value })}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              >
                {CATEGORIES?.map((cat) => (
                  <option key={cat} value={cat}>{cat?.charAt(0)?.toUpperCase() + cat?.slice(1)?.replace('-', ' ')}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Frameworks/Tags */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Topics / Frameworks</label>
            <input
              type="text"
              value={form?.frameworks}
              onChange={(e) => setForm({ ...form, frameworks: e?.target?.value })}
              placeholder="Strategic Planning, Leadership, Management (comma-separated)"
              className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>

          {/* External Link */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">External Link</label>
            <input
              type="url"
              value={form?.external_link}
              onChange={(e) => setForm({ ...form, external_link: e?.target?.value })}
              placeholder="https://..."
              className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>

          {/* Badges */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form?.is_new}
                onChange={(e) => setForm({ ...form, is_new: e?.target?.checked })}
                className="w-4 h-4 rounded border-border text-primary"
              />
              <span className="text-sm text-foreground">Mark as New</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form?.is_bestseller}
                onChange={(e) => setForm({ ...form, is_bestseller: e?.target?.checked })}
                className="w-4 h-4 rounded border-border text-primary"
              />
              <span className="text-sm text-foreground">Mark as Bestseller</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || uploadingCover}
              className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading || uploadingCover ? (
                <><Icon name="Loader2" size={16} className="animate-spin" /> Saving...</>
              ) : (
                <><Icon name="Save" size={16} /> {isEdit ? 'Update Book' : 'Add Book'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookFormModal;
